import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { ChatMessage } from '../../api/types'
import { IconCheck, IconCopy, IconChevronDown, IconLogo } from '../icons'
import { Markdown } from './Markdown'
import { CitationModal } from './CitationModal'
import { linkCitations, type KbPassageRef } from './citations'

const COLLAPSE_CHAR_LIMIT = 350
const COLLAPSE_LINE_LIMIT = 8

// Reasoning is shown by the feed's step group ("Thought · N words"), not here.
/**
 * `showActions` (default on) controls the copy row under an assistant reply. The feed turns it off for
 * replies in the middle of a turn, so the row's reserved height does not push the next step away.
 */
export function MessageItem({ message, streaming, citations, showHeader, showActions = true }: { message: ChatMessage; streaming: boolean; citations?: Map<number, KbPassageRef>; showHeader?: boolean; showActions?: boolean }) {
  const { t, i18n } = useTranslation()
  const [copied, setCopied] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [cited, setCited] = useState<KbPassageRef | null>(null)

  const handleCopy = useCallback(() => {
    if (!message.text) return
    void navigator.clipboard?.writeText(message.text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [message.text])

  if (message.role === 'user') {
    const userText = message.displayText ?? message.text
    const isLongText = userText.length > COLLAPSE_CHAR_LIMIT || userText.split('\n').length > COLLAPSE_LINE_LIMIT

    return (
      <div className="flex flex-col items-end gap-2">
        {/* Attachments sit above the bubble as small bordered thumbnails (docs/web-ui.md §3). */}
        {message.images?.length ? (
          <div className="flex flex-wrap justify-end gap-2">
            {message.images.map(img => (
              <img
                key={img.id}
                src={img.dataUrl}
                alt={img.name}
                className="max-h-40 rounded-[10px] border border-line bg-cream-50 object-cover"
              />
            ))}
          </div>
        ) : null}
        <div className="max-w-[85%] rounded-[18px] rounded-br-[4px] bg-cream-200 px-4 py-2.5 text-[15px] leading-6 text-ink-100">
          <div className="relative">
            <div className={`whitespace-pre-wrap ${!expanded && isLongText ? 'max-h-40 overflow-hidden' : ''}`}>
              {userText}
            </div>
            {!expanded && isLongText ? (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-cream-200 to-transparent" />
            ) : null}
          </div>
          {isLongText ? (
            <div className="mt-1 flex justify-start border-t border-cream-300 pt-1">
              <button
                type="button"
                onClick={() => setExpanded(e => !e)}
                className="flex items-center gap-1 text-xs text-ink-500 transition-colors hover:text-ink-100 focus:outline-none"
              >
                <IconChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
                <span>{expanded ? t('common.showLess') : t('common.showMore')}</span>
              </button>
            </div>
          ) : null}
        </div>
      </div>
    )
  }
  return (
    <div className="group/msg space-y-1.5">
      {showHeader ? <AssistantHeader createdAt={message.createdAt} locale={i18n.language} /> : null}
      {message.text ? <Markdown text={citations ? linkCitations(message.text, citations) : message.text} onCite={citations ? n => setCited(citations.get(n) ?? null) : undefined} /> : null}
      {streaming && <span className="inline-block h-4 w-2 animate-pulse rounded-sm bg-ginger-500 align-middle" />}
      {showActions && !streaming && message.text ? (
        <div className="flex items-center gap-1 pt-0.5 opacity-100 transition-opacity md:opacity-0 md:group-hover/msg:opacity-100 focus-within:opacity-100 [@media(hover:none)]:opacity-100">
          <button
            type="button"
            onClick={handleCopy}
            title={copied ? t('common.copied') : t('common.copy')}
            aria-label={copied ? t('common.copied') : t('common.copy')}
            className="flex items-center gap-1.5 rounded px-2 py-1 text-xs text-ink-500 transition-colors hover:bg-cream-100 hover:text-ink-100 active:bg-cream-200 focus:outline-none"
          >
            {copied ? (
              <>
                <IconCheck className="h-3.5 w-3.5 text-mint-500" />
                <span className="text-[11px] text-mint-500">{t('common.copied')}</span>
              </>
            ) : (
              <>
                <IconCopy className="h-3.5 w-3.5" />
                <span className="text-[11px]">{t('common.copy')}</span>
              </>
            )}
          </button>
        </div>
      ) : null}
      <CitationModal passage={cited} onClose={() => setCited(null)} />
    </div>
  )
}

/** "Meow · 09:14" above the first block of an assistant turn (a reply or a step group). */
export function AssistantHeader({ createdAt, locale }: { createdAt?: number | string; locale: string }) {
  return (
    <div className="flex items-center gap-2">
      <IconLogo className="h-6 w-6 shrink-0 rounded-[7px] object-cover" alt="" aria-hidden="true" />
      <span className="text-[13.5px] font-semibold text-ink-100">Meow</span>
      {createdAt !== undefined && (
        <span className="font-mono text-[11px] tabular-nums text-ink-500">
          {new Date(createdAt).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}
        </span>
      )}
    </div>
  )
}
